import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  TableContainer,
  HStack,
  Icon,
  Tooltip,
  VStack,
  Collapse,
  IconButton,
  Button
} from '@chakra-ui/react'
import { FC, useEffect, useMemo, useState } from 'react'
import {
  FiUser,
  FiHome,
  FiShoppingBag,
  FiTruck,
  FiPackage,
  FiCalendar,
  FiClock,
  FiInfo,
  FiChevronDown,
  FiChevronUp,
  FiMaximize2,
  FiMinimize2
} from 'react-icons/fi'

import usePurchaseSchedule from '@hook/dashboard/use-purchase-schedule'

import type { IPurchaseScheduleData } from '@base/@types/providers/Dashboard/PurchaseScheduleProvider'

import { STATUS_CONFIG, formatDateBR, getRowStatus } from './utils'

// Paginação no client: evita renderizar milhares de linhas de uma vez
const PAGE_SIZE = 50

const getRowKey = (row: IPurchaseScheduleData) =>
  [
    row.empresaId,
    row.lojaId,
    row.fornecedorId,
    row.categoriaCompactaId,
    row.compradorEmail ?? ''
  ].join('-')

const DataTable: FC = () => {
  const { data, statusCounts } = usePurchaseSchedule()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isCompact, setIsCompact] = useState(false)
  const [page, setPage] = useState(0)

  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE))

  // Nova busca volta para a primeira página
  useEffect(() => {
    setPage(0)
  }, [data])

  const pageRows = useMemo(
    () => data.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [data, page]
  )

  // Um único status por linha, com a mesma regra do statusCounts
  const renderStatus = (row: IPurchaseScheduleData) => {
    const cfg = STATUS_CONFIG[getRowStatus(row)]
    return (
      <Tooltip label={cfg.label}>
        <HStack spacing={1} justify="center">
          <Icon as={cfg.icon} color={`${cfg.color}.500`} aria-hidden />
          <Text fontSize="xs" color={`${cfg.color}.700`} whiteSpace="nowrap">
            {cfg.label}
          </Text>
        </HStack>
      </Tooltip>
    )
  }

  const formatDate = (date: string) => formatDateBR(date)

  return (
    <Box
      bg="white"
      p={4}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      shadow="md"
      overflow="hidden"
    >
      <HStack
        justify="space-between"
        mb={4}
        pb={2}
        borderBottom="1px solid"
        borderColor="gray.100"
      >
        <HStack spacing={2}>
          <Icon as={FiPackage} color="blue.500" />
          <Text fontSize="lg" fontWeight="bold" color="gray.700">
            Controle de Fornecedores
          </Text>
        </HStack>
        <HStack spacing={2}>
          <Badge colorScheme="blue">
            {data.length} {data.length === 1 ? 'registro' : 'registros'}
          </Badge>
          <IconButton
            aria-label={isCompact ? 'Expandir tabela' : 'Compactar tabela'}
            icon={<Icon as={isCompact ? FiMaximize2 : FiMinimize2} />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setIsCompact(!isCompact)}
          />
          <IconButton
            aria-label={isCollapsed ? 'Mostrar tabela' : 'Ocultar tabela'}
            icon={<Icon as={isCollapsed ? FiChevronDown : FiChevronUp} />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setIsCollapsed(!isCollapsed)}
          />
        </HStack>
      </HStack>

      <Collapse in={!isCollapsed} animateOpacity>
        <TableContainer
          borderRadius="md"
          overflow="hidden"
          border="1px solid"
          borderColor="gray.200"
        >
          <Table variant="simple" size="sm">
            <Thead bg="gray.100">
              <Tr>
                <Th>
                  <HStack spacing={1}>
                    <Icon as={FiUser} w={4} h={4} />
                    <Text>Comprador</Text>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={1}>
                    <Icon as={FiHome} w={4} h={4} />
                    <Text>Empresa</Text>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={1}>
                    <Icon as={FiShoppingBag} w={4} h={4} />
                    <Text>Loja</Text>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={1}>
                    <Icon as={FiTruck} w={4} h={4} />
                    <Text>Fornecedor</Text>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={1}>
                    <Icon as={FiPackage} w={4} h={4} />
                    <Text>Categoria</Text>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={1}>
                    <Icon as={FiCalendar} w={4} h={4} />
                    <Text>Frequência</Text>
                  </HStack>
                </Th>
                <Th>
                  <HStack spacing={1}>
                    <Icon as={FiCalendar} w={4} h={4} />
                    <Text>Próxima Compra</Text>
                  </HStack>
                </Th>
                <Th textAlign="center">
                  <HStack spacing={1} justify="center">
                    <Icon as={FiClock} w={4} h={4} />
                    <Text>Status</Text>
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageRows.map(row => (
                <Tr
                  key={getRowKey(row)}
                  _hover={{ bg: 'gray.50' }}
                  bg={
                    row.emAtraso
                      ? 'red.50'
                      : row.pendencia
                      ? 'yellow.50'
                      : 'green.50'
                  }
                >
                  <Td>
                    {isCompact ? (
                      <Text fontSize="xs" color="gray.700">
                        {(row.compradorEmail ?? '').split('@')[0] || '-'}
                      </Text>
                    ) : (
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" color="gray.700">
                          {(row.compradorEmail ?? '').split('@')[0] || '-'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {(row.compradorEmail ?? '').split('@')[1]}
                        </Text>
                      </VStack>
                    )}
                  </Td>
                  <Td>
                    {isCompact ? (
                      <Text fontSize="xs" color="gray.800">
                        {row.empresaNome}
                      </Text>
                    ) : (
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" color="gray.800">
                          {row.empresaNome}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          ID: {row.empresaId}
                        </Text>
                      </VStack>
                    )}
                  </Td>
                  <Td>
                    {isCompact ? (
                      <Text fontSize="xs" color="gray.700">
                        {row.lojaNome}
                      </Text>
                    ) : (
                      <VStack align="start" spacing={0}>
                        <Text color="gray.700">{row.lojaNome}</Text>
                        <Text fontSize="xs" color="gray.500">
                          ID: {row.lojaId}
                        </Text>
                      </VStack>
                    )}
                  </Td>
                  <Td>
                    {isCompact ? (
                      <Text fontSize="xs" color="gray.700">
                        {row.fornecedorNome}
                      </Text>
                    ) : (
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" color="gray.700">
                          {row.fornecedorNome}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          ID: {row.fornecedorId}
                        </Text>
                      </VStack>
                    )}
                  </Td>
                  <Td>
                    {isCompact ? (
                      <Text fontSize="xs" color="gray.600">
                        {row.categoriaCompactaNome}
                      </Text>
                    ) : (
                      <VStack align="start" spacing={0}>
                        <Text color="gray.600">
                          {row.categoriaCompactaNome}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          ID: {row.categoriaCompactaId}
                        </Text>
                      </VStack>
                    )}
                  </Td>
                  <Td>
                    {isCompact ? (
                      <Text fontSize="xs" color="gray.600">
                        {row.frequencia}d
                      </Text>
                    ) : (
                      <HStack>
                        <Icon as={FiCalendar} color="blue.500" />
                        <Text color="gray.600">{row.frequencia} dias</Text>
                      </HStack>
                    )}
                  </Td>
                  <Td>
                    {isCompact ? (
                      <Text fontSize="xs" color="gray.600">
                        {formatDate(row.proximaCompraPrevista)}
                      </Text>
                    ) : (
                      <HStack>
                        <Icon as={FiCalendar} color="blue.500" />
                        <Text color="gray.600">
                          {formatDate(row.proximaCompraPrevista)}
                        </Text>
                      </HStack>
                    )}
                  </Td>
                  <Td textAlign="center">{renderStatus(row)}</Td>
                </Tr>
              ))}

              {/* Linha de totais */}
              <Tr bg="gray.100" fontWeight="bold">
                <Td colSpan={6}>
                  <Text fontWeight="bold" color="gray.700" textAlign="right">
                    Total Geral:
                  </Text>
                </Td>
                <Td>
                  <Text fontWeight="bold" color="gray.700">
                    {statusCounts?.total}{' '}
                    {statusCounts?.total === 1 ? 'registro' : 'registros'}
                  </Text>
                </Td>
                <Td textAlign="center">
                  <HStack justify="center" spacing={4}>
                    <VStack>
                      <Text fontSize="xs" color="red.600">
                        Atrasados
                      </Text>
                      <Badge colorScheme="red" variant="solid" px={2} py={1}>
                        {statusCounts.emAtraso}
                      </Badge>
                    </VStack>
                    <VStack>
                      <Text fontSize="xs" color="yellow.600">
                        Pendentes
                      </Text>
                      <Badge colorScheme="yellow" variant="solid" px={2} py={1}>
                        {statusCounts.pendente}
                      </Badge>
                    </VStack>
                    <VStack>
                      <Text fontSize="xs" color="green.600">
                        No Prazo
                      </Text>
                      <Badge colorScheme="green" variant="solid" px={2} py={1}>
                        {statusCounts.noPrazo}
                      </Badge>
                    </VStack>
                  </HStack>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </TableContainer>

        {data.length > PAGE_SIZE && (
          <HStack justify="flex-end" spacing={3} mt={3}>
            <Text fontSize="sm" color="gray.600">
              {(page * PAGE_SIZE + 1).toLocaleString('pt-BR')}–
              {Math.min((page + 1) * PAGE_SIZE, data.length).toLocaleString(
                'pt-BR'
              )}{' '}
              de {data.length.toLocaleString('pt-BR')} · Página {page + 1} de{' '}
              {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              isDisabled={page === 0}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              isDisabled={page >= totalPages - 1}
            >
              Próxima
            </Button>
          </HStack>
        )}
      </Collapse>

      {/* Resumo */}
      {!isCollapsed && (
        <Box
          mt={4}
          p={isCompact ? 2 : 4}
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <HStack justify="space-between" align="center" mb={2}>
            <HStack spacing={2}>
              <Icon as={FiInfo} color="blue.500" />
              <Text
                fontSize={isCompact ? 'sm' : 'md'}
                fontWeight="bold"
                color="gray.700"
              >
                Resumo do Controle
              </Text>
            </HStack>
          </HStack>
          <HStack spacing={isCompact ? 3 : 6} flexWrap="wrap">
            <HStack spacing={2}>
              <HStack spacing={1} bg="blue.100" px={2} py={1} borderRadius="md">
                <Icon
                  as={FiPackage}
                  color="blue.500"
                  boxSize={isCompact ? 3 : 4}
                />
                <Text fontSize={isCompact ? 'xs' : 'sm'} color="gray.700">
                  <strong>Total:</strong> {statusCounts?.total}
                </Text>
              </HStack>
            </HStack>
            <HStack spacing={2}>
              <HStack spacing={1} bg="red.100" px={2} py={1} borderRadius="md">
                <Icon
                  as={STATUS_CONFIG.emAtraso.icon}
                  color="red.500"
                  boxSize={isCompact ? 3 : 4}
                />
                <Text fontSize={isCompact ? 'xs' : 'sm'} color="red.600">
                  <strong>Atrasados:</strong> {statusCounts.emAtraso}
                </Text>
              </HStack>
            </HStack>
            <HStack spacing={2}>
              <HStack
                spacing={1}
                bg="yellow.100"
                px={2}
                py={1}
                borderRadius="md"
              >
                <Icon
                  as={STATUS_CONFIG.pendente.icon}
                  color="yellow.500"
                  boxSize={isCompact ? 3 : 4}
                />
                <Text fontSize={isCompact ? 'xs' : 'sm'} color="yellow.600">
                  <strong>Pendentes:</strong> {statusCounts.pendente}
                </Text>
              </HStack>
            </HStack>
            <HStack spacing={2}>
              <HStack
                spacing={1}
                bg="green.100"
                px={2}
                py={1}
                borderRadius="md"
              >
                <Icon
                  as={STATUS_CONFIG.noPrazo.icon}
                  color="green.500"
                  boxSize={isCompact ? 3 : 4}
                />
                <Text fontSize={isCompact ? 'xs' : 'sm'} color="green.600">
                  <strong>No Prazo:</strong> {statusCounts.noPrazo}
                </Text>
              </HStack>
            </HStack>
          </HStack>
        </Box>
      )}
    </Box>
  )
}

export default DataTable
